/// <amd-module name="Widgets/TeamMembersWidget/components/TeamMembersWidget" />
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetProps, WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import {
    IInviteUsersInternalContext,
    InviteUsersContext,
    InviteUsersContextProvider
} from "TFS/Views/ProjectOverviewPage/Components/InviteUsersContext";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { localeFormat } from "VSS/Core/Util/String";
import { IdentityDetailsProvider } from "VSS/Features/Identities/IdentityDetailsProvider";
import { ILegacyPlatformService, LegacyComponent } from "VSS/Legacy/Legacy";
import { IdentityRef, TeamMember } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { IVssPersonaProps, VssPersonaSize } from "VSSUI/VssPersona";
import { IVssPageService } from "VSS/Platform/Page";
import { OpStoreTeamQuery } from "Widgets/Configs/Common/OpStoreTeamQuery";
import * as Resources from "Widgets/TeamMembersWidget/Resources";
import { InviteAFriendComponent } from "./InviteAFriendComponent";
import { PersonaCollection } from "./PersonaCollection";
import { TeamMembersSettings } from "./TeamMembersSettings";
import { TeamMemberData, TeamMembersWidgetDataProvider } from "./TeamMembersWidgetDataProvider";

export interface AddUserDialogProps {
    showAddUsersDialog: boolean;
    onAddUsersActionComplete?(): void;
    onAddUserDialogLoadStarted?(): void;
    onAddUserDialogLoadCompleted?(): void;
    onAddUserDialogDismiss?(): void;
}

interface TeamMembersWidgetState {
    showAddMembersDialog: boolean;
    teamMembers: IdentityRef[];
    totalMemberCount: number;
    dialogIsOpen: boolean;
    team: WidgetTeamContext;
}

export class TeamMembersWidget extends VssComponent<WidgetProps, TeamMembersWidgetState> {
    public static componentType = "teamMembersWidget";
    public static maxDisplayedTeamMembers = 10;

    private dataProvider: TeamMembersWidgetDataProvider;
    private isOnPrem?: boolean;

    public constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props, context);
        if (context) {
            this.dataProvider = new TeamMembersWidgetDataProvider(context.pageContext);

            const pageData = context.pageContext.getService<IVssPageService>("IVssPageService").getData();
            this.isOnPrem = !pageData?.isHosted
        }
        this.state = {
            teamMembers: [],
            totalMemberCount: 0,
            showAddMembersDialog: false,
            dialogIsOpen: false,
            team: props.teamContext
        };
    }

    public render(): JSX.Element | null {
        if (this.state.team == null) {
            return null;
        }

        const hasTeamMembers = this.state.teamMembers.length > 1;
        const renderedComponent = !hasTeamMembers ? this.renderInviteAFriendComponent() : this.renderTeamMembersView();
        const project = TfsWidgetContext.getProject(this.context.pageContext);
        return (
            <div className="team-members-widget">
                <VisualTitle text={this.getTitleText()} />
                <InviteUsersContextProvider
                    projectOrTeamDisplayName={this.state.team.name}
                    isOnPrem={this.isOnPrem}
                    projectId={project.id}
                    teamId={this.state.team.id}
                    onUsersAdded={() => {
                        const teamId: string = this.state.team ? this.state.team.id : this.props.teamContext.id;
                        const ignoreCache = true;
                        this.getTeamMembersData(teamId, ignoreCache).then(data => {
                            this.setState({ teamMembers: data.members, totalMemberCount: data.count.value });
                        });
                    }}
                >
                    {renderedComponent}
                    {this.renderAddMemberDialog()}
                </InviteUsersContextProvider>
            </div>
        );
    }

    public getMemberTotalDisplay(total: number): string {
        if (total > 100) {
            return "100+";
        }
        return total.toString();
    }

    public async componentDidMount(): Promise<void> {
        this.loadComponent(true);
    }

    public async componentDidUpdate(prevProps: WidgetProps, prevState: TeamMembersWidgetState): Promise<void> {
        const configCancelled = prevState.team.id === this.state.team.id && 
                                this.props.teamContext.id !== this.state.team.id;

        this.loadComponent(false, configCancelled);
    }

    private async getSelectedTeam(): Promise<WidgetTeamContext> {
        let selectedTeam: WidgetTeamContext = this.state.team;
        if (this.props.settings != null) {
            const parsedSettings: TeamMembersSettings = JSON.parse(this.props.settings);
            if (this.settingsAreValid(parsedSettings)) {
                // use the cacheable query service to get the team assoc with the id
                const queryService: ICacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>(
                    IDashboardsCacheableQueryServiceName
                );
                const query: OpStoreTeamQuery = OpStoreTeamQuery.onProject(
                    TfsWidgetContext.getProject(this.context.pageContext).id,
                    parsedSettings.teamId
                );
                selectedTeam = await queryService.getCacheableQueryResult(query);
            }
        }

        return selectedTeam != null ? selectedTeam : this.props.teamContext;
    }

    private async loadComponent(isMounting: boolean, configCancelled?: boolean): Promise<void> {
        try {
            const selectedTeam: WidgetTeamContext = configCancelled ? this.props.teamContext: await this.getSelectedTeam();

            if (this.state.team !== selectedTeam || isMounting) {
                const ignoreCache = false;
                const data: TeamMemberData = await this.getTeamMembersData(selectedTeam.id, ignoreCache);
                this.setState({ teamMembers: data.members, totalMemberCount: data.count.value, team: selectedTeam });

                this.props.onWidgetLoaded();
                WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
            }
        } catch (error) {
            this.props.onWidgetLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: error
            });
        }
    }

    /*  This method checks for a bug we encountered when we added a config. 
    *   For an unknown reason, some pre-existing Team Members widgets have props.settings = "{}",
    *   which resulted in settings being defined, but without a teamId. 
    */
    private settingsAreValid(parsedSettings: TeamMembersSettings): boolean {
        return parsedSettings.teamId != null;
    }

    private getMaxTeamMembersDisplayed() {
        let max = TeamMembersWidget.maxDisplayedTeamMembers;
        if (TfsWidgetContext.isAdmin(this.context.pageContext)) {
            max--;
        }
        return max;
    }

    private getTitleText(): string {
        let title = this.state.team.name;
        const displayedMembers = this.getMaxTeamMembersDisplayed();
        const totalMembers = this.getMemberTotalDisplay(this.state.totalMemberCount);
        if (this.state.totalMemberCount > displayedMembers) {
            title += " " + localeFormat(Resources.ShowingFormat, displayedMembers, totalMembers);
        }
        return title;
    }

    private buildPersonaCollectionProps(members: IdentityRef[]): IVssPersonaProps[] {
        return members.map(member => {
            return {
                imgAltText: member.displayName,
                size: "large" as VssPersonaSize,
                dataIsFocusable: true,
                isTabStop: true,
                identityDetailsProvider: new IdentityDetailsProvider(this.context.pageContext, this.buildIdentityRef(member))
            };
        });
    }

    private buildIdentityRef(member: IdentityRef): TeamMember {
        return {
            isTeamAdmin: false,
            identity: member
        };
    }

    private getTeamMembersData(teamId: string, ignoreCache?: boolean): Promise<TeamMemberData> {
        if (this.dataProvider && teamId) {
            return this.dataProvider.getTeamMembers(teamId, ignoreCache).then(data => {
                this.shuffle(data.members);
                return data;
            });
        }

        return Promise.reject("Team Members data is unavailable.");
    }

    private renderInviteAFriendComponent(): JSX.Element {
        return (
            <InviteUsersContext.Consumer>
                {inviteUserContext => (
                    <InviteAFriendComponent
                        userIsAdmin={TfsWidgetContext.isAdmin(this.context.pageContext)}
                        onManageMembersClick={() => {
                            WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, { linkDescription: "InviteFriend" });
                            this.onAddMembersClick(inviteUserContext);
                        }}
                    />
                )}
            </InviteUsersContext.Consumer>
        );
    }

    private renderTeamMembersView(): JSX.Element {
        const addMembersHandler = TfsWidgetContext.isAdmin(this.context.pageContext) ? this.onAddMembersClick : undefined;
        const showAllMembersHandler = TfsWidgetContext.isAdmin(this.context.pageContext) ? this.onViewAllClick : undefined;

        const shouldShowViewAllButton: boolean = this.getMaxTeamMembersDisplayed() < this.state.teamMembers.length && !!showAllMembersHandler;
        return (
            <InviteUsersContext.Consumer>
                {inviteUserContext => (
                    <>
                        {shouldShowViewAllButton && (
                            <Button
                                className="view-all"
                                text={Resources.ViewAll}
                                onClick={() => {
                                    WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, { linkDescription: "ViewAll" });
                                    showAllMembersHandler && showAllMembersHandler();
                                }}
                            />
                        )}
                        <PersonaCollection
                            personas={this.buildPersonaCollectionProps(this.state.teamMembers)}
                            limit={this.getMaxTeamMembersDisplayed()}
                            onClickAddButton={
                                TfsWidgetContext.isAdmin(this.context.pageContext)
                                    ? () => {
                                          WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, { linkDescription: "AddButton" });
                                          addMembersHandler && addMembersHandler(inviteUserContext);
                                      }
                                    : undefined
                            }
                        />
                    </>
                )}
            </InviteUsersContext.Consumer>
        );
    }

    /**
     * Renders "manage team members" dialog in case of on prem and loads the "invite users" side panel in case of hosted
     * Reloads team members after dialog close.
     */
    private onAddMembersClick = (inviteUsersContext: IInviteUsersInternalContext | undefined): void => {
        if (TfsWidgetContext.isHosted(this.context.pageContext)) {
            if (inviteUsersContext && !inviteUsersContext.useLegacyComponent) {
                inviteUsersContext.onFlowStart();
            } else {
                this.setState({
                    showAddMembersDialog: true
                });
            }
        } else if (!this.state.dialogIsOpen) {
            this.loadManageMembershipControl();
        }
    };

    /**
     * Renders "manage team members" dialog that shows all team members and also allows adding of members and reloads team members after dialog close.
     */
    private onViewAllClick = (): void => {
        if (!this.state.dialogIsOpen) {
            this.loadManageMembershipControl();
        }
    };

    private loadManageMembershipControl() {
        const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
        const dependencyPromises = legacyService.requireModules(["Admin/Scripts/TFS.Admin.Registration.HostPlugins", "VSS/Events/Action"]);
        this.setState({ dialogIsOpen: true });
        dependencyPromises.then(
            ([dialogModule, events_Action]) => {
                const manageTeamMembersProps = {
                    teamName: this.state.team.name,
                    teamId: this.state.team.id,
                    callback: (membersModified: boolean) => {
                        if (membersModified) {
                            const teamId = this.state.team ? this.state.team.id : this.props.teamContext.id;
                            const ignoreCache = true;
                            this.getTeamMembersData(teamId, ignoreCache).then(data => {
                                this.setState({
                                    teamMembers: data.members,
                                    totalMemberCount: data.count.value,
                                    dialogIsOpen: false
                                });
                            });
                        } else {
                            this.setState({
                                dialogIsOpen: false
                            });
                        }
                    }
                };
                events_Action.getService().performAction("manage-team-members", manageTeamMembersProps);
            },
            error => {
                error;
                this.setState({
                    dialogIsOpen: false
                });
            }
        );
    }

    private renderAddMemberDialog(): JSX.Element {
        return (
            <InviteUsersContext.Consumer>
                {inviteUserContext => {
                    if (inviteUserContext && inviteUserContext.useLegacyComponent) {
                        const addUserDialogProps: AddUserDialogProps = {
                            showAddUsersDialog: this.state.showAddMembersDialog,
                            onAddUserDialogDismiss: () => {
                                const teamId = this.state.team ? this.state.team.id : this.props.teamContext.id;
                                this.setState({ showAddMembersDialog: false });
                                const ignoreCache = true;
                                this.getTeamMembersData(teamId, ignoreCache).then(data =>
                                    this.setState({ teamMembers: data.members, totalMemberCount: data.count.value })
                                );
                            }
                        };

                        return (
                            <div className="add-user-dialog-container" role="presentation">
                                <LegacyComponent
                                    wrappedType="TFS.ProjectOverview.AddUserDialog"
                                    modules={["ProjectOverview/Scripts/Components/AddUserDialogComponent"]}
                                    wrappedProps={addUserDialogProps}
                                />
                            </div>
                        );
                    }
                    return null;
                }}
            </InviteUsersContext.Consumer>
        );
    }

    private shuffle(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }
}

VssComponent.register(TeamMembersWidget.componentType, TeamMembersWidget);
